<template>
  <div class="flex min-h-screen bg-gray-50">
    <Sidebar
      @open-create-modal="openCreateModal"
      @logout="logout"
    />

    <!-- Main Content -->
    <div class="lg:ml-64 p-4 sm:p-6 lg:p-8 w-full">
      <!-- Back Button -->
      <div class="mb-4">
        <button @click="goBack" class="flex items-center text-blue-600 hover:text-blue-800 transition-colors">
          <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
      </div>

      <h1 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">Request Details</h1>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="bg-white rounded-lg p-8 shadow-sm">
          <div class="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p class="text-gray-600">Loading request details...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-8">
        <div class="bg-white rounded-lg p-8 shadow-sm">
          <svg class="w-16 h-16 mx-auto text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-lg font-medium text-gray-800">Error Loading Request</p>
          <p class="text-sm text-gray-600 mt-2">{{ error }}</p>
          <button @click="fetchRequest" class="mt-4 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
            Try Again
          </button>
        </div>
      </div>

      <!-- Request Detail Card -->
      <div v-else-if="request" class="max-w-4xl">
        <div class="bg-white rounded-lg shadow-md border border-gray-100">
          <div class="p-6">
            <!-- Header -->
            <div class="flex flex-col sm:flex-row justify-between items-start mb-6 gap-3">
              <div class="flex-1">
                <h3 class="text-xl font-semibold text-gray-800 mb-2">
                  Request #{{ request._id.slice(-6).toUpperCase() }}
                </h3>
                <div class="flex flex-wrap items-center gap-2">
                  <span class="px-3 py-1 text-sm font-medium rounded-full"
                        :class="getPriorityClass(request.priority)">
                    {{ request.priority || 'Medium' }}
                  </span>
                  <span class="px-3 py-1 text-sm font-medium rounded-full"
                        :class="getStatusClass(request.status)">
                    {{ getStatusLabel(request.status) }}
                  </span>
                  <span class="text-sm text-gray-500">{{ formatDate(request.createdAt) }}</span>
                </div>
              </div>
              <div class="text-left sm:text-right">
                <p class="text-3xl font-bold text-gray-900">RF {{ formatAmount(request.amount) }}</p>
                <p class="text-sm text-gray-500">{{ request.paymentMethod || 'Bank Transfer' }}</p>
              </div>
            </div>

            <!-- Request Details Grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div>
                <p class="text-gray-500 text-sm mb-1">Requester</p>
                <p class="font-medium text-gray-900">{{ request.requester?.name || 'Unknown User' }}</p>
                <p class="text-sm text-gray-600">{{ request.requester?.email || '' }}</p>
              </div>
              <div>
                <p class="text-gray-500 text-sm mb-1">Department</p>
                <p class="font-medium text-gray-900">{{ request.department || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-gray-500 text-sm mb-1">Beneficiary</p>
                <p class="font-medium text-gray-900">{{ request.supplier || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-gray-500 text-sm mb-1">Beneficiary Account/MoMo</p>
                <p class="font-medium text-gray-900">{{ request.beneficiaryAccount || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-gray-500 text-sm mb-1">Preferred Payment Date</p>
                <p class="font-medium text-gray-900">{{ formatDate(request.preferredPaymentDate) }}</p>
              </div>
            </div>

            <!-- Purpose -->
            <div class="mb-6">
              <p class="text-gray-500 text-sm mb-2">Purpose</p>
              <p class="text-gray-900 bg-gray-50 p-4 rounded-lg">{{ request.purpose }}</p>
            </div>

            <!-- Approval Path Visualization -->
            <div class="mb-6">
              <p class="text-gray-500 text-sm mb-2">Approval Workflow</p>
              <ApprovalPathVisualization
                :amount="request.amount"
                :approvalPath="request.approvalPath"
                :status="request.status"
              />
            </div>

            <!-- Comments Section -->
            <div v-if="hasComments(request)" class="mb-6">
              <p class="text-gray-500 text-sm mb-3">Review Comments</p>
              <div class="space-y-3">
                <!-- Finance Review Comments -->
                <div v-if="request.financeReview && request.financeReview.comments"
                     class="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r">
                  <div class="flex items-center mb-2">
                    <div class="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    <span class="text-sm font-semibold text-blue-700">Finance Review</span>
                    <span class="text-xs text-blue-600 ml-auto">{{ formatDate(request.financeReview.reviewedAt) }}</span>
                  </div>
                  <p class="text-sm text-blue-900 mb-2">{{ request.financeReview.comments }}</p>
                  <div class="text-xs text-blue-700 space-y-1">
                    <div v-if="request.financeReview.reviewedBy">
                      Reviewed by: <span class="font-medium">{{ request.financeReview.reviewedBy.name }}</span>
                    </div>
                    <div v-if="request.financeReview.treasuryStatus">
                      Treasury Status: <span class="font-medium">{{ request.financeReview.treasuryStatus }}</span>
                    </div>
                    <div v-if="request.financeReview.budgetAvailable !== undefined">
                      Budget Available: <span class="font-medium">{{ request.financeReview.budgetAvailable ? 'Yes' : 'No' }}</span>
                    </div>
                    <div v-if="request.financeReview.cashflowImpact">
                      Cashflow Impact: <span class="font-medium">{{ request.financeReview.cashflowImpact }}</span>
                    </div>
                  </div>
                </div>

                <!-- MD Review Comments -->
                <div v-if="request.mdReview && request.mdReview.comments"
                     class="bg-purple-50 border-l-4 border-purple-400 p-4 rounded-r">
                  <div class="flex items-center mb-2">
                    <div class="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                    <span class="text-sm font-semibold text-purple-700">Managing Director Review</span>
                    <span class="text-xs text-purple-600 ml-auto">{{ formatDate(request.mdReview.reviewedAt) }}</span>
                  </div>
                  <p class="text-sm text-purple-900 mb-2">{{ request.mdReview.comments }}</p>
                  <div class="text-xs text-purple-700 space-y-1">
                    <div v-if="request.mdReview.reviewedBy">
                      Reviewed by: <span class="font-medium">{{ request.mdReview.reviewedBy.name }}</span>
                    </div>
                    <div v-if="request.mdReview.operationalNeedConfirmed !== undefined">
                      Operational Need Confirmed: <span class="font-medium">{{ request.mdReview.operationalNeedConfirmed ? 'Yes' : 'No' }}</span>
                    </div>
                    <div v-if="request.mdReview.approvedAmount && request.mdReview.approvedAmount !== request.amount">
                      Approved Amount: <span class="font-medium">RF {{ formatAmount(request.mdReview.approvedAmount) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Attachments -->
            <div v-if="hasAttachments(request)" class="mb-6">
              <p class="text-gray-500 text-sm mb-2">Attachments</p>
              <div class="flex flex-wrap gap-2">
                <button v-if="request.attachments?.invoice"
                        @click="downloadAttachment(request._id, 'invoice')"
                        class="text-sm bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors">
                  📄 Invoice
                </button>
                <button v-if="request.attachments?.contract"
                        @click="downloadAttachment(request._id, 'contract')"
                        class="text-sm bg-green-100 text-green-700 px-3 py-2 rounded-lg hover:bg-green-200 transition-colors">
                  📋 Contract
                </button>
                <button v-for="(doc, index) in request.attachments?.otherDocuments || []"
                        :key="index"
                        @click="downloadAttachment(request._id, `other-${index}`)"
                        class="text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                  📎 Document {{ index + 1 }}
                </button>
              </div>
            </div>

            <!-- Action Buttons (only show if user can approve and request is pending) -->
            <div v-if="canTakeAction()" class="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
              <button @click="openApprovalModal(true)"
                      class="flex-1 bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 focus:ring-2 focus:ring-green-300 transition-all duration-200 font-medium">
                Approve Request
              </button>
              <button @click="openApprovalModal('reverse')"
                      class="flex-1 bg-amber-500 text-white py-3 px-4 rounded-lg hover:bg-amber-600 focus:ring-2 focus:ring-amber-300 transition-all duration-200 font-medium">
                Send Back for Changes
              </button>
              <button @click="openApprovalModal(false)"
                      class="flex-1 bg-red-500 text-white py-3 px-4 rounded-lg hover:bg-red-600 focus:ring-2 focus:ring-red-300 transition-all duration-200 font-medium">
                Reject Request
              </button>
            </div>

            <!-- Reversed - requester can edit and resubmit -->
            <div v-else-if="canResubmit()" class="pt-4 border-t border-gray-200">
              <div v-if="request.reversal" class="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r mb-4">
                <p class="text-sm font-semibold text-amber-800 mb-1">Sent back for changes</p>
                <p class="text-sm text-amber-900 mb-1">{{ request.reversal.reason }}</p>
                <p v-if="request.reversal.reversedBy" class="text-xs text-amber-700">
                  By {{ request.reversal.reversedBy.name }}<span v-if="request.reversal.reversedByRole"> ({{ formatRole(request.reversal.reversedByRole) }})</span>
                </p>
              </div>
              <button @click="openEditModal"
                      class="w-full bg-amber-600 text-white py-3 px-4 rounded-lg hover:bg-amber-700 focus:ring-2 focus:ring-amber-300 transition-all duration-200 font-medium">
                Edit &amp; Resubmit
              </button>
            </div>

            <!-- Reversed - viewed by someone other than the requester -->
            <div v-else-if="request.status === 'reversed'" class="bg-amber-50 p-4 rounded-lg text-center">
              <p class="text-amber-800">This request was sent back to the requester for changes and is awaiting resubmission.</p>
            </div>

            <!-- Request Already Processed Message -->
            <div v-else-if="request.status !== 'pending'" class="bg-gray-100 p-4 rounded-lg text-center">
              <p class="text-gray-700">This request has already been {{ request.status }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Approval Modal -->
      <div v-if="showApprovalModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">
              {{ approvalAction === true ? 'Approve' : approvalAction === false ? 'Reject' : 'Send Back' }} Request
            </h3>
            <button @click="closeApprovalModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="mb-4 p-3 bg-gray-50 rounded">
            <p class="text-sm text-gray-600">Request #{{ request?._id.slice(-6).toUpperCase() }}</p>
            <p class="font-medium">RF {{ formatAmount(request?.amount) }}</p>
            <p class="text-sm text-gray-600">{{ request?.purpose }}</p>
          </div>

          <p v-if="approvalAction === 'reverse'" class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            This sends the request straight back to the requester to fix or clarify - it is not a rejection. They can edit and resubmit it directly to you.
          </p>

          <!-- Finance Manager Fields -->
          <div v-if="user.role === 'finance' && approvalAction === true">
            <div class="space-y-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Treasury Status</label>
                <select v-model="reviewData.treasuryStatus" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select status</option>
                  <option value="sufficient">Sufficient</option>
                  <option value="limited">Limited</option>
                  <option value="insufficient">Insufficient</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Budget Available</label>
                <select v-model="reviewData.budgetAvailable" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select availability</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Cashflow Impact</label>
                <select v-model="reviewData.cashflowImpact" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select impact</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Managing Director Fields -->
          <div v-if="user.role === 'managing_director' && approvalAction === true">
            <div class="space-y-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Operational Need Confirmed</label>
                <select v-model="reviewData.operationalNeedConfirmed" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select confirmation</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Approved Amount</label>
                <input v-model.number="reviewData.approvedAmount"
                       type="number"
                       :placeholder="request?.amount"
                       class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              </div>
            </div>
          </div>

          <!-- Comments / Reversal Reason -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ approvalAction === 'reverse' ? 'Reason for sending back *' : 'Comments' }}
            </label>
            <textarea v-model="reviewData.comments"
                      rows="3"
                      :placeholder="approvalAction === 'reverse' ? 'Explain what needs to change or which documents are missing...' : 'Add your comments...'"
                      class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"></textarea>
          </div>

          <div class="flex space-x-3">
            <button @click="submitAction"
                    :disabled="isSubmitting"
                    :class="approvalAction === true ? 'bg-green-500 hover:bg-green-600' : approvalAction === false ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'"
                    class="flex-1 text-white py-2 px-4 rounded-lg transition-all duration-200 font-medium disabled:opacity-50">
              {{ isSubmitting ? 'Processing...' : (approvalAction === true ? 'Approve' : approvalAction === false ? 'Reject' : 'Send Back') }}
            </button>
            <button @click="closeApprovalModal"
                    class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200">
              Cancel
            </button>
          </div>
        </div>
      </div>

      <!-- Create Request Modal -->
      <div v-if="showCreateModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white bg-opacity-90 backdrop-blur-lg rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <CreateRequest @request-created="closeCreateModal" @close="closeCreateModal" />
        </div>
      </div>

      <!-- Edit & Resubmit Modal -->
      <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white bg-opacity-90 backdrop-blur-lg rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <CreateRequest mode="edit" :existingRequest="request" @request-resubmitted="handleResubmitted" @close="closeEditModal" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CreateRequest from '../components/CreateRequest.vue';
import Sidebar from '../components/Sidebar.vue';
import ApprovalPathVisualization from '../components/ApprovalPathVisualization.vue';

export default {
  components: { CreateRequest, Sidebar, ApprovalPathVisualization },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user') || '{}'),
      request: null,
      loading: true,
      error: null,
      showCreateModal: false,
      showEditModal: false,
      showApprovalModal: false,
      approvalAction: null, // true for approve, false for reject, 'reverse' for send back
      isSubmitting: false,
      reviewData: {
        // Finance fields
        treasuryStatus: '',
        budgetAvailable: '',
        cashflowImpact: '',
        // MD fields
        operationalNeedConfirmed: '',
        approvedAmount: null,
        // Common fields
        comments: ''
      }
    };
  },
  async mounted() {
    await this.fetchRequest();
  },
  methods: {
    async fetchRequest() {
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const requestId = this.$route.params.id;

        const res = await axios.get(
          `https://guarantee.iwacufinance.com/cash-request/api/requests/${requestId}`,
          { headers }
        );
        this.request = res.data;
      } catch (error) {
        console.error('Error fetching request:', error);
        this.error = error.response?.data?.message || 'Failed to load request details';
      } finally {
        this.loading = false;
      }
    },

    canTakeAction() {
      // Check if user is an approver and the request is pending
      if (!this.request || this.request.status !== 'pending') {
        return false;
      }

      // Check if user is an approver (not employee)
      const approverRoles = ['finance', 'managing_director','direct_manager', 'hod', 'ceo', 'admin'];
      return approverRoles.includes(this.user.role);
    },

    canResubmit() {
      if (!this.request || this.request.status !== 'reversed') {
        return false;
      }
      return !!this.request.requester && String(this.request.requester._id) === String(this.user.id);
    },

    openEditModal() {
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
    },

    async handleResubmitted() {
      await this.fetchRequest();
      this.closeEditModal();
      alert('Request resubmitted successfully');
    },

    formatRole(role) {
      const roleMap = {
        direct_manager: 'Manager',
        hod: 'HoD',
        managing_director: 'MD',
        ceo: 'CEO',
        finance: 'Finance',
        admin: 'Admin',
        employee: 'Employee'
      };
      return roleMap[role] || role;
    },

    openApprovalModal(approve) {
      this.approvalAction = approve;
      this.showApprovalModal = true;
      this.resetReviewData();
      // Pre-populate approved amount with request amount for MD
      if (this.user.role === 'managing_director') {
        this.reviewData.approvedAmount = this.request.amount;
      }
    },

    closeApprovalModal() {
      this.showApprovalModal = false;
      this.approvalAction = null;
      this.resetReviewData();
    },

    resetReviewData() {
      this.reviewData = {
        treasuryStatus: '',
        budgetAvailable: '',
        cashflowImpact: '',
        operationalNeedConfirmed: '',
        approvedAmount: null,
        comments: ''
      };
    },

    async submitAction() {
      if (this.approvalAction === 'reverse' && !this.reviewData.comments?.trim()) {
        alert('Please provide a reason for sending this request back');
        return;
      }

      try {
        this.isSubmitting = true;
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

        if (this.approvalAction === 'reverse') {
          await axios.post(
            `https://guarantee.iwacufinance.com/cash-request/api/requests/${this.request._id}/reverse`,
            { reason: this.reviewData.comments },
            { headers }
          );
        } else {
          const payload = {
            approve: this.approvalAction,
            ...this.reviewData
          };

          await axios.post(
            `https://guarantee.iwacufinance.com/cash-request/api/requests/${this.request._id}/action`,
            payload,
            { headers }
          );
        }

        // Refresh the request data
        await this.fetchRequest();
        this.closeApprovalModal();

        // Show success message
        const actionLabel = this.approvalAction === 'reverse' ? 'sent back' : (this.approvalAction ? 'approved' : 'rejected');
        alert(`Request ${actionLabel} successfully`);

        // Redirect to approvals list after short delay
        setTimeout(() => {
          this.$router.push('/approvals');
        }, 1500);
      } catch (error) {
        console.error('Error processing action:', error);
        alert('Failed to process action: ' + (error.response?.data?.message || error.message));
      } finally {
        this.isSubmitting = false;
      }
    },

    async downloadAttachment(requestId, type) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `https://guarantee.iwacufinance.com/cash-request/api/requests/attachment/${requestId}/${type}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${type}-${requestId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        alert('Failed to download attachment');
      }
    },

    hasAttachments(request) {
      return request.attachments && (
        request.attachments.invoice ||
        request.attachments.contract ||
        (request.attachments.otherDocuments && request.attachments.otherDocuments.length > 0)
      );
    },

    hasComments(request) {
      return (
        (request.financeReview && request.financeReview.comments) ||
        (request.mdReview && request.mdReview.comments)
      );
    },

    getPriorityClass(priority) {
      switch (priority?.toLowerCase()) {
        case 'high':
          return 'bg-red-100 text-red-800';
        case 'medium':
          return 'bg-yellow-100 text-yellow-800';
        case 'low':
          return 'bg-green-100 text-green-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },

    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'approved':
          return 'bg-green-100 text-green-800';
        case 'rejected':
          return 'bg-red-100 text-red-800';
        case 'reversed':
          return 'bg-amber-100 text-amber-800';
        case 'pending':
          return 'bg-yellow-100 text-yellow-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },

    getStatusLabel(status) {
      if (status === 'reversed') return 'Sent Back';
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-RW').format(amount || 0);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-RW', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    goBack() {
      // Go back to previous page, or default to dashboard
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push('/dashboard');
      }
    },

    openCreateModal() {
      this.showCreateModal = true;
    },

    closeCreateModal() {
      this.showCreateModal = false;
    },

    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
